import { useState, useRef, useEffect, type ChangeEvent, type KeyboardEvent } from 'react';
import { Plus, ChevronDown, Search } from 'lucide-react';
import { useClickOutside } from '../../../../hooks/useClickOutside';

export interface APExam {
  id: number | string;
  name: string;
  requires_advisor?: boolean;
}

export interface APUserScore {
  ap_exam_id: number | string;
  [key: string]: unknown;
}

export interface APScoreFormProps {
  apExams: APExam[];
  userScores: APUserScore[];
  saving?: boolean;
  onAddScore: (
    examId: number,
    score: number,
    year: number | null
  ) => Promise<void> | void;
}

const APScoreForm = ({ apExams, userScores, saving, onAddScore }: APScoreFormProps) => {
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedScore, setSelectedScore] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [examSearch, setExamSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const getAvailableExams = (): APExam[] => {
    const scoredExamIds = new Set(userScores.map((score) => score.ap_exam_id));
    return apExams.filter((exam) => !scoredExamIds.has(exam.id));
  };

  const getFilteredExams = (): APExam[] => {
    const available = getAvailableExams();
    if (!examSearch) return available;
    const query = examSearch.toLowerCase();
    return available.filter((exam) => exam.name.toLowerCase().includes(query));
  };

  const getYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let year = currentYear; year >= currentYear - 10; year--) {
      years.push(year);
    }
    return years;
  };

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        (items[highlightedIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleExamSelect = (exam: APExam): void => {
    setSelectedExam(String(exam.id));
    setExamSearch(exam.name + (exam.requires_advisor ? ' *' : ''));
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setExamSearch(e.target.value);
    setSelectedExam('');
    setIsDropdownOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = (): void => {
    setIsDropdownOpen(true);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    const filtered = getFilteredExams();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isDropdownOpen) {
          setIsDropdownOpen(true);
        } else {
          setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
          handleExamSelect(filtered[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const toggleDropdown = (): void => {
    setIsDropdownOpen((prev) => !prev);
    if (!isDropdownOpen) {
      inputRef.current?.focus();
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!selectedExam || !selectedScore) return;

    try {
      await onAddScore(
        parseInt(selectedExam),
        parseInt(selectedScore),
        selectedYear ? parseInt(selectedYear) : null
      );
      setSelectedExam('');
      setSelectedScore('');
      setSelectedYear('');
      setExamSearch('');
    } catch {
      // Parent already handled the error toast
    }
  };

  const filteredExams = getFilteredExams();

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-3">Add AP Score</h3>
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">AP Exam</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={examSearch}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onKeyDown={handleKeyDown}
                placeholder="Search or select an AP exam"
                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={saving}
                role="combobox"
                aria-controls="ap-exam-listbox"
                aria-expanded={isDropdownOpen}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={toggleDropdown}
                disabled={saving}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
                aria-label="Toggle exam list"
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDropdownOpen && (
                <ul
                  ref={listRef}
                  id="ap-exam-listbox"
                  role="listbox"
                  aria-label="AP exam options"
                  className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                  {filteredExams.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-gray-500">No matching exams found</li>
                  ) : (
                    filteredExams.map((exam, index) => (
                      <li
                        key={exam.id}
                        role="option"
                        aria-selected={selectedExam === String(exam.id)}
                        className={`px-3 py-2 text-sm cursor-pointer ${
                          index === highlightedIndex
                            ? 'bg-primary text-white'
                            : selectedExam === String(exam.id)
                              ? 'bg-red-50 text-gray-900'
                              : 'text-gray-900 hover:bg-gray-100'
                        }`}
                        onClick={() => handleExamSelect(exam)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        {exam.name}
                        {exam.requires_advisor && ' *'}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Score</label>
            <select
              value={selectedScore}
              onChange={(e) => setSelectedScore(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={saving}
            >
              <option value="">Score</option>
              <option value="5">5</option>
              <option value="4">4</option>
              <option value="3">3</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year (Optional)</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={saving}
            >
              <option value="">Year</option>
              {getYearOptions().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-gray-500">
            * Language exams require advisor consultation
          </div>
          <button
            onClick={handleSubmit}
            disabled={!selectedExam || !selectedScore || saving}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Score</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default APScoreForm;
